import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import useHotelApi from "utils/axiosInstance";
import { showError, showSuccess } from "utils/notifications";

import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import SearchInput from "Common/DataTable/SearchInput";
import EntriesSelector from "Common/DataTable/EntriesSelector";
import Pagination from "Common/DataTable/Pagination";
import { updateLanguage } from "adminApi/LanguageApi/Language";
import { deletePhrase } from "adminApi/PhraseListApi/PhraseList";
import { useRoleVerify } from "Common/RoleVerify";

import { validateField } from "utils/validation";
import { showConfirmBox } from "utils/ConfirmHelper";
import EditableInput from "./EditableInput";
import { PhraseTranslationRules } from "validation/ValidationRules";


const PhraseListTable = ({ refresh = false }) => {
  const HotelApi = useHotelApi();
  const { id: language } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const parentpage = location.pathname.replace('/admin/', '').split('/')[0] || "";
  const canDelete = useRoleVerify(parentpage, ["can_delete"]);

  const [dataT, setDataT] = useState([]);
  const [editedValues, setEditedValues] = useState({});
  const [errors, setErrors] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 500);

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);

  const [meta, setMeta] = useState({
    totalPages: 1,
    prevPage: false,
    nextPage: false,
    totalDocs: 0,
    page: 1,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sizeOptions = [10, 20, 50, 100, 500];

  //  Search
  const handleSearch = useCallback((newValue, resetPage = null) => {
    setSearch(newValue);
    if (resetPage !== null) setPage(resetPage);
  }, []);

  //  Build API URL
  const makeUrl = useCallback(
    (pageNo, pageLimit, searchQuery) => {
      const url = new URL(
        `/language/getphrase/${language}`,
        window.location.origin
      );
      url.search = new URLSearchParams({
        page: String(pageNo),
        limit: String(pageLimit),
        search: searchQuery ?? "",
      }).toString();
      return url.pathname + url.search;
    },
    [language]
  );

  //  Fetch phrases
  const fetchPhrases = useCallback(
    async (signal = null) => {
      setLoading(true);
      setError(null);
      try {
        const url = makeUrl(page, limit, debouncedSearch);
        const res = await HotelApi.get(url, signal);
        if (signal && signal.aborted) return;

        const data = res.data;
        setDataT(Array.isArray(data.data) ? data.data : []);
        setMeta({
          totalPages: data.totalPages ?? 1,
          prevPage: Boolean(data.prevPage),
          nextPage: Boolean(data.nextPage),
          totalDocs: data.totalDocs ?? 0,
          page: data.page ?? 1,
        });
      } catch (err) {
        if (err.name !== "CanceledError") {
          setError(err.message || "Unknown error");
        }
      } finally {
        if (!signal || !signal.aborted) setLoading(false);
      }
    },
    [HotelApi, makeUrl, page, limit, debouncedSearch]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchPhrases(controller.signal);
    return () => controller.abort();
  }, [fetchPhrases, refresh]);

  //  Pagination & Limit
  const onPageChange = useCallback(
    (newPage) => setPage((p) => (p === newPage ? p : newPage)),
    []
  );
  const onLimitChange = useCallback((newLimit) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  //  Edit cell
  const handleInputChange = useCallback((id, value) => {
    setEditedValues((prev) => ({
      ...prev,
      [id]: value,
    }));

    // Live validation
    const errorMsg = validateField("translation", value, PhraseTranslationRules);
    setErrors((prev) => ({
      ...prev,
      [id]: errorMsg,
    }));
  }, []);

  //  Delete phrase
  const handleDelete = async (phraseId, phraseName) => {
    const confirmed = await showConfirmBox(
      `Are you sure you want to delete the phrase "${phraseName}" in all languages ? This action cannot be undone.`,
      "Confirm Delete"
    );
    if (!confirmed) return;

    setDeletingId(phraseId);
    try {
      const response = await deletePhrase(phraseId);
      if (response.data.success) {
        showSuccess(response.data.message || "Phrase deleted successfully");
        await fetchPhrases(); // refresh data
      } else {
        showError(response.data.error || "Error deleting phrase");
      }
    } catch (err) {
      console.error("Delete failed", err);
      showError(err.response?.data?.message || "Error deleting phrase");
    } finally {
      setDeletingId(null);
    }
  };

  //  Save all edits
  const handleSave = async () => {
    // Validate before save
    const validationErrors = {};
    Object.entries(editedValues).forEach(([id, value]) => {
      const errorMsg = validateField("translation", value, PhraseTranslationRules);
      if (errorMsg) validationErrors[id] = errorMsg;
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      Object.values(validationErrors).forEach((msg) => showError(msg));
      return;
    }

    const updates = Object.entries(editedValues).map(([id, value]) => ({
      id,
      [language]: value,
    }));

    if (updates.length === 0) return;

    try {
      const response = await updateLanguage({
        id: language,
        updates: updates,
      });

      if (response.data.success === true || response.data.status === 201) {
        showSuccess(response.data.message || "Language updated successfully");
        await fetchPhrases(); // refresh data
        setEditedValues({});
        setErrors({});
        navigate(-1);
      } else {
        showError(response.data.error || "Error updating language");
      }
    } catch (err) {
      console.error("Update failed", err);
      showError(err.response?.data?.message || "Error updating language");
    }
  };

  //  Format table data
  const formattedData = useMemo(() => {
    return dataT.map((u, index) => ({
      rowNo: (page - 1) * limit + index + 1,
      phrase: u.phrase,
      value: editedValues[u.id] ?? u.value ?? "",
      phrase_id: u.id,
      error: errors[u.id] ?? "",
    }));
  }, [dataT, page, limit, editedValues, errors]);

  //  Table columns
  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "rowNo" },
      { Header: "Phrase", accessor: "phrase" },
      {
        Header: `Translation in ${language}`,
        accessor: "translation",
        Cell: ({ row }) => (
          <EditableInput
            phraseId={row.original.phrase_id}
            value={row.original.value}
            error={row.original.error}
            onChange={handleInputChange}
          />
        ),
      },
      ...(canDelete ? [{
        Header: "Action",
        accessor: "actions",
        Cell: ({ row }) => (
          <button
            className="btn btn-danger btn-sm"
            onClick={() => handleDelete(row.original.phrase_id, row.original.phrase)}
            disabled={deletingId === row.original.phrase_id}
            title="Delete phrase"
          >
            {deletingId === row.original.phrase_id ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-trash"></i>
            )}
          </button>
        ),
      }] : []),
    ],
    [handleInputChange, language, canDelete, deletingId]
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <EntriesSelector
          limit={limit}
          onLimitChange={onLimitChange}
          options={sizeOptions}
        />
        <div className="d-flex align-items-center">
          <label htmlFor="search" className="me-2">Search:</label>
          <SearchInput value={search} page={page} onChange={handleSearch} />
        </div>
      </div>

      <div aria-live="polite" className="mb-2">
        {loading && <span>Loading phrases…</span>}
        {error && <span className="text-danger">Error: {error}</span>}
      </div>

      <DataTable
        columns={columns}
        data={formattedData}
        getRowId={(row) => row.phrase_id}
        pagination={{
          isActive: false,
          meta,
          onPageChange,
        }}
      />

      <div className="d-flex justify-content-between align-items-center mt-3 gap-3 flex-wrap">
        <div>
          Page {meta.page} of {meta.totalPages} — Total {meta.totalDocs} records
        </div>
        <Pagination
          page={meta.page}
          totalPages={meta.totalPages}
          hasPrev={meta.prevPage}
          hasNext={meta.nextPage}
          onPageChange={onPageChange}
        />
      </div>

      <div className="d-flex justify-content-end mt-3 sticky-bottom bg-white p-2 border-top">
        <button
          className="btn btn-primary"
          type="button"
          onClick={handleSave}
          disabled={Object.keys(editedValues).length === 0}
        >
          Save All Changes
        </button>
      </div>
    </>
  );
};

export default PhraseListTable;


